"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { type Locale, defaultLocale, locales } from '@/lib/i18n';
import { track } from '@/lib/telemetry/posthog';
import enMessages from '@/messages/en.json';

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('elportal_locale') as Locale;
      if (saved && locales.includes(saved)) return saved;
    }
    return defaultLocale;
  });

  const [messages, setMessages] = useState<Record<string, any>>(enMessages);

  // Load messages when locale changes
  useEffect(() => {
    if (locale === 'en') {
      setMessages(enMessages);
      return;
    }
    import(`@/messages/${locale}.json`)
      .then((mod) => setMessages(mod.default))
      .catch(() => setMessages(enMessages));
  }, [locale]);

  // Update html lang attribute
  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem('elportal_locale', locale);
  }, [locale]);

  // Sync from DB on mount (async, non-blocking)
  useEffect(() => {
    const syncFromDb = async () => {
      try {
        const { supabaseService } = await import('@/lib/supabaseService');
        const settings = await supabaseService.getUserSettings();
        const dbLocale = settings.language as Locale;
        if (dbLocale && locales.includes(dbLocale) && dbLocale !== locale) {
          setLocaleState(dbLocale);
        }
      } catch {
        // Not authenticated or network error — ignore
      }
    };
    syncFromDb();
  }, []);

  const setLocale = useCallback(async (newLocale: Locale) => {
    setLocaleState(newLocale);
    track('settings_changed', { setting_id: 'locale' });
    try {
      const { supabaseService } = await import('@/lib/supabaseService');
      await supabaseService.updateUserSettings({ language: newLocale });
    } catch {
      // Silently fail — localStorage still has the value
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ locale, setLocale }}>
      <NextIntlClientProvider
        locale={locale}
        messages={messages}
        timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
        onError={process.env.NODE_ENV === 'development' ? (e) => console.warn('i18n:', e) : () => { /* prod: silent — getMessageFallback handles display */ }}
        getMessageFallback={({ key }) => {
          // Walk the English messages to find the fallback
          const parts = key.split('.');
          let fallback: any = enMessages;
          for (const part of parts) {
            fallback = fallback?.[part];
          }
          return typeof fallback === 'string' ? fallback : key;
        }}
      >
        {children}
      </NextIntlClientProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
